import moment from "moment";
import {reportData} from "@/utils/scoutingReportData";
export {
	saveReport,
	deleteReport,
	saveTemplate,
	removeTemplate,
} from "@/utils/scoutingReportData";
// Relazioni scouting a template (contratto BE in docs/scouting-api-contract.md, tappa 4).

export const FIELD_TYPES = [
	{id: "score", label: "Voto 1–5"},
	{id: "text", label: "Testo"},
	{id: "choice", label: "Scelta"},
	{id: "attitude", label: "Attitudine"},
];

export const VERDICTS = [
	{
		id: "take",
		label: "Da prendere",
		color: "var(--color-good)",
		bg: "var(--color-good-bg)",
	},
	{
		id: "follow",
		label: "Da seguire",
		color: "var(--color-warning)",
		bg: "var(--color-warning-bg)",
	},
	{
		id: "unfit",
		label: "Non idoneo",
		color: "var(--color-critical)",
		bg: "var(--color-critical-bg)",
	},
];

export const getVerdict = (id) => VERDICTS.find((v) => v.id === id) || null;

const field = (id, label, type, extra = {}) => ({id, label, type, ...extra});

export const DEFAULT_TEMPLATES = [
	{
		id: "standard",
		name: "Standard",
		fields: [
			field("technique", "Tecnica", "score"),
			field("tactics", "Tattica", "score"),
			field("physical", "Fisico", "score"),
			field("mentality", "Mentalità", "score"),
			field("attitudes", "Attitudine", "attitude"),
			field("strengths", "Punti di forza", "text"),
			field("weaknesses", "Punti deboli", "text"),
		],
	},
	{
		id: "goalkeeper",
		name: "Portiere",
		fields: [
			field("shot_stopping", "Tra i pali", "score"),
			field("crosses", "Uscite", "score"),
			field("footwork", "Gioco con i piedi", "score"),
			field("communication", "Comunicazione", "score"),
			field("personality", "Personalità", "score"),
			field("strengths", "Punti di forza", "text"),
			field("weaknesses", "Punti deboli", "text"),
		],
	},
];

// La relazione conserva una copia del template: modificarlo dopo non cambia le relazioni scritte.
export const templateSnapshot = (template) => ({
	id: template?.id || null,
	name: template?.name || "",
	fields: (template?.fields || []).map((f) => ({
		...f,
		options: f.options
			? f.options.map((o) => String(o).trim()).filter(Boolean)
			: undefined,
	})),
});

// La scelta iniziale dipende dal tipo di giocatore, non dall'ordine alfabetico del catalogo.
export const defaultReportTemplate = (templates, goalkeeper) =>
	(goalkeeper &&
		templates.find((template) => template.seedCode === "goalkeeper")) ||
	templates.find((template) => template.seedCode === "standard") ||
	templates[0];

export const countWords = (text) =>
	String(text || "")
		.trim()
		.split(/\s+/)
		.filter(Boolean).length;

const isScore = (value) => Number.isInteger(value) && value >= 1 && value <= 5;

// Media dei voti 1–5 della relazione, null se non ci sono voti.
export const reportAverage = (report) => {
	const scores = (report?.template?.fields || [])
		.filter((f) => f.type === "score")
		.map((f) => report.values?.[f.id])
		.filter(isScore);
	if (!scores.length) return null;
	return scores.reduce((sum, v) => sum + v, 0) / scores.length;
};

// Media delle medie delle relazioni di un giocatore.
export const playerReportsAverage = (reports) => {
	const averages = (reports || []).map(reportAverage).filter((v) => v !== null);
	if (!averages.length) return null;
	return averages.reduce((sum, v) => sum + v, 0) / averages.length;
};

export const scoreTone = (value) =>
	value >= 4 ? "good" : value < 3 ? "bad" : "neutral";

// Errori di compilazione: verdetto e giudizio testuale sono sempre obbligatori.
export const validateReport = (report) => {
	const errors = [];
	if (!getVerdict(report?.verdict)) errors.push("verdict");
	if (!countWords(report?.judgment)) errors.push("judgment");
	(report?.template?.fields || [])
		.filter((f) => f.required)
		.forEach((f) => {
			const value = report.values?.[f.id];
			const empty = Array.isArray(value)
				? !value.length
				: value === undefined || value === null || value === "";
			if (empty) errors.push(f.id);
		});
	return errors;
};

// ── Modifica dei template ──────────────────────────────────────────────────

export const validateTemplate = (template) => {
	const errors = [];
	if (!String(template?.name || "").trim()) errors.push("name");
	(template?.fields || []).forEach((f) => {
		if (!String(f.label || "").trim()) errors.push(f.id);
		else if (
			f.type === "choice" &&
			(f.options || []).filter((o) => String(o).trim()).length < 2
		)
			errors.push(f.id);
	});
	return errors;
};

export const moveField = (fields, index, offset) => {
	const target = index + offset;
	if (target < 0 || target >= fields.length) return fields;
	const next = [...fields];
	[next[index], next[target]] = [next[target], next[index]];
	return next;
};

export const newFieldId = (fields) => {
	const ids = new Set((fields || []).map((f) => f.id));
	let n = ids.size + 1;
	while (ids.has("field_" + n)) n++;
	return "field_" + n;
};

// Le vecchie chiavi browser restano conservate per un recupero esplicito in SCI-11.
export const TEMPLATES_STORAGE_KEY = "scouting_report_templates_v1";
export const REPORTS_STORAGE_KEY = "scouting_reports_v1";
export const loadTemplates = () => reportData().templates;
export const loadReports = () => reportData().reports;
export const getReport = (id) =>
	reportData().details[id] || loadReports().find((r) => r.id === id) || null;
export const playerReports = (playerId) =>
	loadReports().filter((r) => r.playerId === playerId);

// Riepilogo per giocatore: numero, media, ultimo verdetto (forma di /Players/{id}/scouting-summary).
export const reportSummaries = (reports = loadReports()) => {
	const byPlayer = {};
	reports.forEach((r) => (byPlayer[r.playerId] ||= []).push(r));
	return Object.fromEntries(
		Object.entries(byPlayer).map(([playerId, list]) => [
			playerId,
			{
				reportsCount: list.length,
				average: playerReportsAverage(list),
				lastVerdict: list[0].verdict,
				lastReportDate: list[0].reportDate,
				lastJudgment: list[0].judgment,
			},
		]),
	);
};

// Sintesi delle statistiche al momento del salvataggio: ultima stagione dei dati importati.
export const statsSnapshot = (seasonRow) => {
	const t = seasonRow?.totals;
	if (!t?.matches) return null;
	return {
		seasonName: seasonRow.season,
		matches: t.matches,
		minutes: t.minutes || 0,
		goals: t.goals || 0,
		assists: t.assists || 0,
		xg: t.xg ?? null,
	};
};

export const formatAverage = (value) =>
	value === null || value === undefined
		? "—"
		: value.toFixed(1).replace(".", ",");

// Gara da cui nasce la relazione, come la mostra la scheda di dettaglio.
export const reportMatch = (match) =>
	match?.id
		? {
				id: match.id,
				name: [
					[match.homeClub, match.awayClub].filter(Boolean).join(" – "),
					match.startDate && moment(match.startDate).format("DD/MM/YY"),
				]
					.filter(Boolean)
					.join(" · "),
			}
		: null;

export const reportPlayerName = (player) =>
	[player?.lastName, player?.firstName].filter(Boolean).join(" ") ||
	"Giocatore";
